import {
  InlineLoading,
  InlineNotification,
  Tile,
  DataTableSkeleton,
  Layer,
} from '@carbon/react';
import {
  ColumnDef,
  SortingState,
  ColumnPinningState,
  createColumnHelper,
  getExpandedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  getCoreRowModel,
  useReactTable,
  Row,
  flexRender,
} from '@tanstack/react-table';
import { EmptyState } from 'components/empty-state';
import DevAreaSvg from 'images/srp-development-areas.svg';
import { useAtomValue } from 'jotai';
import { usePackageSlotsAtom } from './atoms';
import style from './units.module.scss';
import { PackageSlot } from 'types/packages-api/responses';
import { useCallback, useMemo, useRef, useState, Fragment } from 'react';
import usePinnedColumnReordering from 'lib/hooks/usePinnedColumnReordering';
import { useColumnState } from 'lib/hooks/useColumnState';
import {
  createFilterFn,
  getInitialColumnOrder,
  getPinningStyles,
  handleDragEnd,
  handleDragMove,
} from './utils';
import { useColumnFilter } from 'lib/hooks/useColumnFilter';
import { useColumnSizing } from 'lib/hooks/useColumnSizing';
import { useColumnVisibility } from 'lib/hooks/useColumnVisibilityAtom';
import { DndContext, DragMoveEvent, closestCenter } from '@dnd-kit/core';
import { MIN_WIDTH } from 'components/table/column-actions';
import { useVirtualizer } from '@tanstack/react-virtual';
import { LoadPackage } from '../page';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { DebouncedInput } from 'components/debounced-input';
import { TableActions } from 'components/table/table-actions';
import {
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { DragCell, DragHeader } from './draggable-table-components';
import { ColumnFilter } from 'components/table/column-filter';
import { HiddenColumns } from 'components/table/hidden-columns';
import { DECIMAL_FORMATTER, PERCENT_FORMATTER_TWO_DECIMALS } from 'lib/ui';
import { format0 } from 'utils/number';
import plur from 'plur';
import { Link } from 'components/link';

const columnHelper = createColumnHelper<PackageSlot>();

const Slots = ({ slotsHref }: { slotsHref: string }) => {
  const packageSlotsAtom = usePackageSlotsAtom(slotsHref);
  const packageSlots = useAtomValue(packageSlotsAtom);

  if (packageSlots.data && 'error' in packageSlots.data)
    return (
      <InlineNotification
        kind="error"
        title={packageSlots.data.error}
        lowContrast
      />
    );

  if (packageSlots.loading)
    return (
      <InlineLoading
        className={style.paddingLoading}
        description="Loading slots..."
      />
    );

  if (!packageSlots.data?.slots.length)
    return (
      <Layer>
        <Tile>
          <EmptyState
            icon={DevAreaSvg}
            headerText="No slots available for this development area"
          />
        </Tile>
      </Layer>
    );

  const slotsData = packageSlots.data?.slots.map((el, i) => {
    return {
      id: String(i),
      ...el,
    };
  });

  const columns = [
    columnHelper.accessor((row) => row.uniqueSlotId, {
      id: 'uniqueSlotId',
      header: 'Slot ID',
      size: 200,
      // Slot ID renders as either a UWI (well-bound slot) or a Slot Ref
      // like `U-0000005807_BoneSpring 1st Sand_2` (LTD / non-well slot).
      // The BE pre-computes the right link target on each row's `slotHref`
      // — well page for UWIs, development area for Slot Refs — so the
      // cell just renders whatever the BE gave us.
      cell: ({ row }) => {
        if (row.getIsGrouped()) return row.original.uniqueSlotId;
        const value = row.original.uniqueSlotId;
        if (!value) return '--';
        const href = row.original.slotHref;
        if (!href) return value;
        return (
          <Link to={href} target="_blank">
            {value}
          </Link>
        );
      },
    }),
    columnHelper.accessor((row) => row.developmentArea?.name, {
      id: 'developmentAreaName',
      header: 'Development Area',
      size: 125,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.developmentArea?.name || '--';
        const b = rowB.original.developmentArea?.name || '--';
        return a > b ? 1 : a < b ? -1 : 0;
      },
      cell: ({ row }) => {
        const value = row.original.developmentArea;
        return !row.getIsGrouped() && value?.href ? (
          <Link to={value.href} target="_blank">
            {value.name || '--'}
          </Link>
        ) : (
          value?.name || '--'
        );
      },
    }),
    columnHelper.accessor((row) => row.developmentArea?.stateAbbreviation, {
      id: 'stateAbbreviation',
      header: 'State',
      size: 100,
    }),
    columnHelper.accessor((row) => row.slotCategory, {
      id: 'slotCategory',
      header: 'Slot Category',
      size: 135,
    }),
    columnHelper.accessor((row) => row.slotNumber, {
      id: 'slotNumber',
      header: 'Slot Number',
      size: 100,
    }),
    columnHelper.accessor((row) => row.well?.uwi, {
      id: 'uwi',
      header: 'UWI',
      size: 125,
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.well?.uwi || '--';
        const b = rowB.original.well?.uwi || '--';
        return a > b ? 1 : a < b ? -1 : 0;
      },
      cell: ({ row }) => {
        const value = row.original.well;
        return !row.getIsGrouped() && value?.href ? (
          <Link to={value.href} target="_blank">
            {value.uwi || '--'}
          </Link>
        ) : (
          value?.uwi || '--'
        );
      },
    }),
    columnHelper.accessor((row) => row.well?.name, {
      id: 'wellName',
      header: 'Well Name',
      size: 200,
    }),
    columnHelper.accessor((row) => row.well?.operatorName, {
      id: 'operatorName',
      header: 'Operator',
      size: 135,
      cell: (info) => info.getValue() || '--',
    }),
    columnHelper.accessor((row) => row.formationName, {
      id: 'formationName',
      header: 'Formation',
      size: 135,
      cell: (info) => info.getValue() || '--',
    }),
    columnHelper.accessor((row) => row.trueVerticalDepthFeet, {
      id: 'trueVerticalDepthFeet',
      header: 'TVD (ft)',
      size: 130,
      cell: (row) => format0(row.getValue()),
      filterFn: createFilterFn('decimalRange'),
      enableGlobalFilter: false,
      meta: {
        filterVariant: 'range',
      },
    }),
    columnHelper.accessor((row) => row.slotDepth, {
      id: 'slotDepth',
      header: 'Slot Depth',
      size: 135,
      cell: (row) => {
        const v = row.getValue();
        if (typeof v !== 'number') return '--';
        return DECIMAL_FORMATTER.format(v);
      },
      filterFn: createFilterFn('decimalRange'),
      enableGlobalFilter: false,
      meta: {
        filterVariant: 'range',
      },
    }),
    columnHelper.accessor((row) => row.spudDate, {
      id: 'spudDate',
      header: 'Spud Date',
      size: 135,
      cell: (info) => info.getValue() || '--',
    }),
    columnHelper.accessor((row) => row.well?.completionDate, {
      id: 'completionDate',
      header: 'Completion Date',
      size: 150,
      cell: (info) => info.getValue() || '--',
    }),
    columnHelper.accessor((row) => row.firstProductionDate, {
      id: 'firstProductionDate',
      header: 'First Production Date',
      size: 150,
      cell: (info) => info.getValue() || '--',
    }),
    columnHelper.accessor((row) => row.shutInDate, {
      id: 'shutInDate',
      header: 'Shut In Date',
      size: 150,
      cell: (info) => info.getValue() || '--',
    }),
    columnHelper.accessor((row) => row.volumesScalingFactor, {
      id: 'volumesScalingFactor',
      header: 'Volume Scaling Factor',
      size: 150,
      cell: (row) => {
        const value = row.getValue();
        return PERCENT_FORMATTER_TWO_DECIMALS.format(value);
      },
      filterFn: createFilterFn('percentageRange'),
      enableGlobalFilter: false,
      meta: {
        filterVariant: 'range',
      },
    }),
    columnHelper.accessor((row) => row.irr, {
      id: 'irr',
      header: 'IRR',
      size: 150,
      cell: (row) => {
        const v = row.getValue();
        return v !== null ? PERCENT_FORMATTER_TWO_DECIMALS.format(v) : '--';
      },
      filterFn: createFilterFn('percentageRange'),
      enableGlobalFilter: false,
      meta: {
        filterVariant: 'range',
      },
    }),
    columnHelper.accessor((row) => row.hurdleRate, {
      id: 'hurdleRate',
      header: 'Hurdle Rate',
      size: 150,
      cell: (row) => {
        const v = row.getValue();
        return v !== null ? PERCENT_FORMATTER_TWO_DECIMALS.format(v) : '--';
      },
      enableGlobalFilter: false,
      meta: {
        filterVariant: 'range',
      },
      filterFn: createFilterFn('percentageRange'),
    }),
    columnHelper.accessor((row) => row.isExcluded, {
      id: 'isExcluded',
      header: 'Excluded',
      size: 100,
      cell: (info) => info.getValue() || '--',
    }),
    columnHelper.accessor((row) => row.oilEurMbbl, {
      id: 'oilEurMbbl',
      header: 'Gross Oil Reserves (Mbbl)',
      size: 150,
      cell: (row) => {
        const v = row.getValue();
        if (typeof v !== 'number') return '--';
        return DECIMAL_FORMATTER.format(v);
      },
      enableGlobalFilter: false,
      meta: {
        filterVariant: 'range',
      },
      filterFn: createFilterFn('decimalRange'),
    }),
    columnHelper.accessor((row) => row.gasEurMmcf, {
      id: 'gasEurMmcf',
      header: 'Gross Gas Reserves (MMcf)',
      size: 150,
      cell: (row) => {
        const v = row.getValue();
        if (typeof v !== 'number') return '--';
        return DECIMAL_FORMATTER.format(v);
      },
      filterFn: createFilterFn('decimalRange'),
      enableGlobalFilter: false,
      meta: {
        filterVariant: 'range',
      },
    }),
    columnHelper.accessor((row) => row.forecastStatus, {
      id: 'forecastStatus',
      header: 'Forecast Status',
      size: 135,
      cell: (info) => info.getValue() || '--',
    }),
    columnHelper.accessor((row) => row.slotVolumeSource, {
      id: 'slotVolumeSource',
      header: 'Volume Source',
      size: 250,
      cell: (info) => {
        const source = info.row.original.slotVolumeSource;
        const typeCurveHeader = info.row.original.typeCurveHeader;
        const forecastStatus = info.row.original.forecastStatus;

        if (typeCurveHeader?.href && forecastStatus === 'type-curve') {
          return (
            <Link to={typeCurveHeader.href} target="_blank">
              {info.getValue()}
            </Link>
          );
        }

        return source || '--';
      },
    }),
    columnHelper.accessor((row) => row.well?.holeDirection, {
      id: 'holeDirection',
      header: 'Hole Direction',
      size: 135,
      cell: (info) => info.getValue() || '--',
    }),
    columnHelper.accessor((row) => row.well?.status, {
      id: 'wellStatus',
      header: 'Well Status',
      size: 125,
      cell: (info) => info.getValue() || '--',
    }),
    columnHelper.accessor((row) => row.well?.srpLandingZone, {
      id: 'srpLandingZone',
      header: 'SRP Landing Zone',
      size: 150,
      cell: (info) => info.getValue() || '--',
    }),
    columnHelper.accessor((row) => row.well?.permitDate, {
      id: 'permitDate',
      header: 'Permit Date',
      size: 125,
      cell: (info) => info.getValue() || '--',
    }),
  ];

  return (
    <div>
      <SlotsTable data={slotsData} columns={columns} />
    </div>
  );
};

const SlotsTable = ({
  data,
  columns,
}: {
  data: PackageSlot[];
  columns: ColumnDef<PackageSlot, any>[];
}) => {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const slotsTableId = 'slots-table';

  const [columnVisibility, setColumnVisibility] =
    useColumnVisibility(slotsTableId);
  const [columnSizing, setColumnSizing] = useColumnSizing(slotsTableId);
  const initialColumnPinning: ColumnPinningState = {
    left: ['uniqueSlotId'],
  };

  const initialColumnOrder = useMemo(
    () => getInitialColumnOrder(columns),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const { columnOrder, setColumnOrder, columnPinning, setColumnPinning } =
    useColumnState({
      tableId: slotsTableId,
      initialColumnOrder,
      initialColumnPinning,
    });

  const {
    columnFilters,
    setColumnFilters,
    isColumnFilterActive,
    setIsColumnFilterActive,
  } = useColumnFilter(slotsTableId);

  const hoveredColumnIdRef = useRef<string | null>(null);

  usePinnedColumnReordering(columnPinning, columnOrder, setColumnOrder);

  const onDragMove = useCallback(
    (e: DragMoveEvent) => {
      handleDragMove(e, hoveredColumnIdRef);
    },
    [hoveredColumnIdRef]
  );

  const table = useReactTable({
    data: data || [],
    columns,
    columnResizeMode: 'onChange',
    defaultColumn: {
      minSize: MIN_WIDTH,
    },
    state: {
      sorting,
      globalFilter,
      columnSizing,
      columnVisibility,
      columnFilters,
      columnOrder,
      columnPinning,
    },
    onColumnFiltersChange: setColumnFilters,
    onColumnSizingChange: setColumnSizing,
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getExpandedRowModel: getExpandedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    initialState: {
      columnPinning: {
        left: ['uniqueSlotId'],
      },
      columnOrder: initialColumnOrder,
    },
    meta: {
      reset: {
        resetCustomizations: () => {
          setIsColumnFilterActive(false);
        },
      },
    },
  });

  const { rows } = table.getRowModel();

  const parentRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 48,
    overscan: 20,
  });

  if (!data) {
    return (
      <DataTableSkeleton
        showHeader={false}
        columnCount={columns.length}
        rowCount={10}
        showToolbar={false}
      />
    );
  }

  const isColumunVisible = table.getVisibleLeafColumns().length > 0;

  return (
    <LoadPackage
      render={() => (
        <>
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={(e) =>
              handleDragEnd(
                e,
                columnPinning,
                columnOrder,
                setColumnOrder,
                setColumnPinning,
                hoveredColumnIdRef
              )
            }
            modifiers={[restrictToHorizontalAxis]}
            onDragMove={onDragMove}
          >
            <div ref={parentRef} className={style.stackTableArea}>
              <div className={style.tableContext}>
                <div className={style.stackSearchArea}>
                  <DebouncedInput
                    className={style.stackSearchField}
                    value={globalFilter ?? ''}
                    onChange={(value) => setGlobalFilter(String(value).trim())}
                    placeholder="Search columns..."
                  />
                </div>
                <TableActions
                  table={table}
                  isColumnFilterActive={isColumnFilterActive}
                  setIsColumnFilterActive={(isColumnFilterActive) =>
                    setIsColumnFilterActive(isColumnFilterActive)
                  }
                ></TableActions>
              </div>
              {isColumunVisible ? (
                <table className={style.stackTable}>
                  <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <Fragment key={headerGroup.id}>
                        <tr>
                          <SortableContext
                            items={columnOrder}
                            strategy={horizontalListSortingStrategy}
                          >
                            {headerGroup.headers.map((header) => (
                              <DragHeader
                                key={header.id}
                                header={header}
                                hoveredColumnIdRef={hoveredColumnIdRef}
                              />
                            ))}
                          </SortableContext>
                        </tr>
                        {isColumnFilterActive ? (
                          <tr>
                            <ColumnFilter
                              pinStyleGenerator={getPinningStyles}
                              headers={headerGroup.headers}
                            />
                          </tr>
                        ) : null}
                      </Fragment>
                    ))}
                  </thead>
                  <tbody
                    style={{ minHeight: `${virtualizer.getTotalSize()}px` }}
                  >
                    {virtualizer.getVirtualItems().map((virtualRow, index) => {
                      const row = rows[virtualRow.index] as Row<PackageSlot>;
                      return (
                        <tr
                          key={row.id}
                          style={{
                            minHeight: `${virtualRow.size}px`,
                            transform: `translateY(${
                              virtualRow.start - index * virtualRow.size
                            }px)`,
                          }}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <SortableContext
                              key={cell.id}
                              items={columnOrder}
                              strategy={horizontalListSortingStrategy}
                            >
                              <DragCell
                                key={cell.id}
                                cell={cell}
                                row={row}
                                hoveredColumnIdRef={hoveredColumnIdRef}
                              />
                            </SortableContext>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    {table.getFooterGroups().map((footerGroup) => (
                      <tr key={footerGroup.id}>
                        {footerGroup.headers.map((header) => (
                          <th
                            style={{
                              ...getPinningStyles(header.column),
                            }}
                            key={header.id}
                            colSpan={header.colSpan}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.footer,
                                  header.getContext()
                                )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </tfoot>
                </table>
              ) : (
                <HiddenColumns className={style.hiddenColumn}></HiddenColumns>
              )}
            </div>
            {isColumunVisible ? (
              <div className={style.totalRowCount}>
                <label className={style.totalRows}>
                  {DECIMAL_FORMATTER.format(
                    table.getFilteredRowModel().rows.length
                  )}
                </label>
                {plur('row', table.getFilteredRowModel().rows.length)}
              </div>
            ) : null}
          </DndContext>
        </>
      )}
    />
  );
};

export { Slots };
